// =============================================================================
// User Types
// =============================================================================

export interface User {
  id: number;
  email: string;
  name: string;
  role: UserRole;
  status: UserStatus;
  themePreference: ThemePreference;
  lockedAt?: string;
  createdAt: string;
  updatedAt: string;
}

// Every member of staff is an 'admin' and can do everything in the backoffice,
// with one exception: only the 'super_admin' may edit, remove or change the
// access of another member of staff. There is one super admin.
//
// 'coordinator' is what self-registered staff were before they became admins;
// it is treated as 'admin' wherever it still turns up. 'user' is kept for
// backwards compatibility with existing fixtures and is not a staff role.
export type UserRole = 'user' | 'admin' | 'coordinator' | 'super_admin';

export type UserStatus = 'active' | 'pending' | 'inactive';

export type ThemePreference = 'light' | 'dark' | 'system';

export interface UserCreateInput {
  email: string;
  password: string;
  name: string;
  phone: string;
}

// Coordinator profile attached to a User (1:1). Populated on register and on
// login when the authenticated user has a coordinator profile.
export interface Coordinator {
  userId: number;
  phone: string;
  createdAt: string;
  updatedAt: string;
}

export interface UserUpdateInput {
  email?: string;
  name?: string;
  themePreference?: ThemePreference;
}

export interface UserUnlockInput {
  newPassword: string;
}

// =============================================================================
// Auth Types
// =============================================================================

// UserKind discriminates the two populations backed by separate API endpoints:
// - staff: web app power users, email + password, stored in the `users` table.
// - participant: PWA end-users (TE-01), ID or email + password, stored in `participants`.
// - guardian: Encarregados de Educação in the PWA, email + password, stored in `guardians`.
export type UserKind = 'staff' | 'participant' | 'guardian';

// StaffLoginInput is accepted by POST /api/auth/staff/login.
export interface StaffLoginInput {
  email: string;
  password: string;
}

// ParticipantLoginInput is accepted by POST /api/auth/participant/login.
// Identifier is either the sequential numeric ID or the participant's email.
export interface ParticipantLoginInput {
  identifier: string;
  password: string;
}

// LoginInput is retained as the legacy staff shape so existing callers keep compiling.
export type LoginInput = StaffLoginInput;

// Participant mirrors the Go model exposed at /api/auth/participant/*.
export type ParticipantGender = 'M' | 'F' | 'I';
export type ParticipantEstadoRegisto = 'Pendente' | 'Ativo' | 'Inativo' | 'Suspenso';

/** Body accepted by POST /api/participants/{id}/suspend */
export interface ParticipantSuspendInput {
  reason: string;
}

/** Body accepted by POST /api/participants/{id}/activate */
export interface ParticipantActivateInput {
  reason: string;
}

/** Body accepted by POST /api/participants/{id}/reset-password */
export interface ParticipantResetPasswordInput {
  newPassword: string;
}

/**
 * Body accepted by PUT /api/auth/me (participant self-service edit).
 * Exposes only the fields a participant may change about themselves —
 * id, email, autorizacaoEe and estadoRegisto are intentionally excluded.
 */
export interface ParticipantSelfUpdateInput {
  nome: string;
  apelido: string;
  genero: ParticipantGender;
  dataNascimento: string; // YYYY-MM-DD
  telemovel?: string | null;
  numeroCc?: string | null;
  habilitacoesLiterarias?: string | null;
  situacaoLaboral?: string | null;
  escola?: string | null;
  eeNome?: string | null;
  eeTelemovel?: string | null;
  eeEmail?: string | null;
  eeNumeroCc?: string | null;
  morada?: string | null;
  localidade?: string | null;
  autorizacaoImagem: boolean;
}

/** Response shape of GET /api/participants */
export interface ListParticipantsResponse {
  participants: Participant[];
  total: number;
  limit: number;
  offset: number;
}

export interface Participant {
  id: number;
  nome: string;
  apelido: string;
  genero: ParticipantGender;
  dataNascimento: string;
  email?: string | null;
  telemovel?: string | null;
  numeroCc?: string | null;
  habilitacoesLiterarias?: string | null;
  situacaoLaboral?: string | null;
  escola?: string | null;
  eeNome?: string | null;
  eeTelemovel?: string | null;
  eeEmail?: string | null;
  eeNumeroCc?: string | null;
  morada?: string | null;
  localidade?: string | null;
  autorizacaoImagem: boolean;
  autorizacaoEe: boolean;
  estadoRegisto: ParticipantEstadoRegisto;
  /**
   * The participant's "centro habitual". Set automatically on their first
   * check-in and editable by staff. Null until then.
   */
  digitalCenterId?: number | null;
  /** Denormalised from the centre; absent when digitalCenterId is null. */
  digitalCenterName?: string | null;
  /**
   * The certificate artwork key of the centro habitual (see
   * CERTIFICATE_TEMPLATES). Absent when the participant has no centre or the
   * centre has no certificate yet — in both cases the PWA shows no
   * certificate actions.
   */
  certificateTemplate?: string | null;
  /** The member of staff who registered them by hand; absent for a PWA sign-up. */
  createdBy?: number | null;
  createdAt: string;
  updatedAt: string;
}

// ParticipantAuthResponse mirrors AuthResponse for participant logins.
export interface ParticipantAuthResponse {
  user: Participant;
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

// ParticipantPendingRegistration is returned when self-registration creates a
// participant in the Pendente state (currently: minors under 18). No tokens
// are issued — the participant must wait for backoffice approval.
export interface ParticipantPendingRegistration {
  participantId: number;
  estadoRegisto: ParticipantEstadoRegisto;
}

// ParticipantRegisterResponse is the discriminated response of
// POST /api/auth/participant/register. Exactly one of active or pending is
// populated, matching `status`.
export type ParticipantRegisterResponse =
  | { status: 'active'; active: ParticipantAuthResponse }
  | { status: 'pending'; pending: ParticipantPendingRegistration };

// ParticipantRegisterInput is accepted by POST /api/auth/participant/register.
// At least one of email or telemovel must be provided so the participant has
// a working password-recovery path; the backend returns a 400 otherwise.
// dataNascimento must be an ISO calendar date (YYYY-MM-DD).
export interface ParticipantRegisterInput {
  nome: string;
  apelido: string;
  genero: ParticipantGender;
  dataNascimento: string;
  email?: string;
  telemovel?: string;
  password: string;
  captchaToken?: string;
}

export interface RegisterInput {
  email: string;
  password: string;
  name: string;
  phone: string;
  captchaToken?: string;
}

export interface AuthResponse {
  user: User;
  coordinator?: Coordinator | null;
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

export interface TokenPair {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

export interface RefreshInput {
  refreshToken: string;
}

export interface ResetPasswordInput {
  token: string;
  newPassword: string;
}

export interface MessageResponse {
  message: string;
}

// =============================================================================
// API Response Types
// =============================================================================

export interface ApiError {
  error: string;
  message: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  limit: number;
  offset: number;
}

export interface ListUsersResponse {
  users: User[];
  total: number;
  limit: number;
  offset: number;
}

// =============================================================================
// Digital Center Types
// =============================================================================

export interface DigitalCenter {
  id: number;
  centerName: string;
  address: string;
  postalCode: string;
  description: string;
  /** Daily opening time in "HH:MM" (24h) format. */
  openingTime: string;
  /** Daily closing time in "HH:MM" (24h) format. Always greater than openingTime. */
  closingTime: string;
  /** Certificate artwork key (see CERTIFICATE_TEMPLATES); null when the centre has none. */
  certificateTemplate?: string | null;
  /** Base64 data URL (data:image/png;base64,...) generated at creation time. */
  qrCode?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface DigitalCenterInput {
  centerName: string;
  address: string;
  postalCode: string;
  description: string;
  openingTime: string; // "HH:MM"
  closingTime: string; // "HH:MM"
  certificateTemplate?: string | null;
}

export interface ListDigitalCentersResponse {
  digitalCenters: DigitalCenter[];
  total: number;
  limit: number;
  offset: number;
}

// Field-level validation errors returned by the API on 422.
export type DigitalCenterValidationErrors = Partial<Record<keyof DigitalCenterInput, string>>;

export interface DigitalCenterValidationResponse {
  message: string;
  errors: DigitalCenterValidationErrors;
}

// =============================================================================
// Equipment Types
// =============================================================================

export type EquipmentEstado = 'Ativo' | 'Inativo' | 'Em utilização' | 'Reservado';

export type EquipmentRequerFormacao = 'Baixa' | 'Média' | 'Alta';

export interface Equipment {
  id: number;
  digitalCenterID: number;
  digitalCenterName: string;
  equipamento: string;
  tipoCategoria: string;
  numeroSerie?: string | null;
  estado: EquipmentEstado;
  descricao: string;
  slotUtilizacao?: number | null;
  /** How many identical units this row represents (>= 1). A pool of 20 micro:bits is one row with quantidade 20. */
  quantidade: number;
  /** Units currently occupied by an open session (derived by the API). Free units = quantidade - emUso. */
  emUso: number;
  comoUtilizar?: string | null;
  manualUtilizador?: string | null; // Base64-encoded PDF
  requerFormacao: EquipmentRequerFormacao;
  descricaoFormacao?: string | null;
  /** UUID of the associated badge (optional). Maps to prerequisite_badge_id. */
  badgeId?: string | null;
  /** Denormalised badge name — populated by the API JOIN. */
  badgeName?: string | null;
  /** Base64 data URL (data:image/png;base64,...) generated at creation time. */
  qrCode?: string | null;
  createdAt: string;
  updatedAt: string;
  // Only present when showDeleted=true
  deletedAt?: string | null;
  deletedBy?: number | null;
  deletionReason?: string | null;
}

export interface EquipmentInput {
  digitalCenterID: number;
  equipamento: string;
  tipoCategoria: string;
  numeroSerie?: string | null;
  estado: EquipmentEstado;
  descricao: string;
  slotUtilizacao?: number | null;
  /** Number of identical units (1–999). Omitted or 0 is treated as 1 by the API. */
  quantidade?: number;
  comoUtilizar?: string | null;
  manualUtilizador?: string | null;
  requerFormacao: EquipmentRequerFormacao;
  descricaoFormacao?: string | null;
  /** UUID of the badge to associate (null = remove association). */
  badgeId?: string | null;
}

/** What a printed sticker for a piece of equipment carries. */
export interface EquipmentLabel {
  id: number;
  name: string;
  category: string;
  estado: EquipmentEstado;
  digitalCenterId: number;
  digitalCenterName: string;
  /** How many units the equipment has. They share the one code. */
  quantity: number;
  /** Base64 data URL (data:image/png;base64,...), drawn for print with no border. */
  qrCode: string;
}

export interface ListEquipmentLabelsResponse {
  labels: EquipmentLabel[];
  total: number;
}

export interface ListEquipmentsResponse {
  equipments: Equipment[];
  total: number;
  limit: number;
  offset: number;
}

export type EquipmentValidationErrors = Partial<Record<keyof EquipmentInput | 'reason', string>>;

export interface EquipmentValidationResponse {
  message: string;
  errors: EquipmentValidationErrors;
}

// =============================================================================
// Badge Types
// =============================================================================

/**
 * The seven DIGGER universes. Brand tokens — never translated.
 */
export type BadgeUniverso = 'Code' | 'Bot' | 'Play' | 'Make' | 'Green' | 'Link' | 'Voice';

export interface Badge {
  id: string; // UUID
  name: string;
  description?: string | null;
  active: boolean;
  // Optional classification — null/absent when the badge is unclassified
  universo?: BadgeUniverso | null;
  labels?: string[] | null;
  createdAt: string;
  updatedAt: string;
  // Only present when showDeleted=true
  deletedAt?: string | null;
  deletedBy?: number | null;
  deletionReason?: string | null;
}

export interface BadgeInput {
  name: string;
  description?: string | null;
  active: boolean;
  universo?: BadgeUniverso | null;
  labels?: string[] | null;
}

export interface ListBadgesResponse {
  badges: Badge[];
  total: number;
  limit: number;
  offset: number;
}

export type BadgeValidationErrors = Partial<Record<keyof BadgeInput | 'reason', string>>;

export interface BadgeValidationResponse {
  message: string;
  errors: BadgeValidationErrors;
}

// =============================================================================
// Participant Badge Types
// =============================================================================

export interface ParticipantBadge {
  id: number;
  participantId: number;
  badgeId: string; // UUID
  badgeName: string;
  // The badge definition's own description, denormalised from the badge
  // definition. This is what the certificate prints — distinct from
  // `description` below, which is the per-assignment note.
  badgeDescription?: string | null;
  // Badge classification, denormalised from the badge definition
  badgeUniverso?: BadgeUniverso | null;
  badgeLabels?: string[] | null;
  assignedAt: string;
  assignedBy: number;
  assignedByName: string;
  description?: string | null;
  // Revocation — null while badge is active
  revokedAt?: string | null;
  revokedBy?: number | null;
  revokedByName?: string | null;
  revocationReason?: string | null;
}

export interface AssignBadgeInput {
  badgeId: string; // UUID
  description?: string | null;
}

export interface RevokeBadgeInput {
  reason?: string | null;
}

export interface ListParticipantBadgesResponse {
  badges: ParticipantBadge[];
  total: number;
}

// =============================================================================
// Message Types
// =============================================================================

export type MessageEstado = 'Ativo' | 'Inativo';

export interface Message {
  id: string; // UUID
  descricao: string;
  dataInicio: string;  // ISO 8601 / RFC3339
  dataFim: string;     // ISO 8601 / RFC3339
  estado: MessageEstado;
  createdAt: string;
  updatedAt: string;
  createdBy?: number | null;
  updatedBy?: number | null;
  // Soft-delete — only present when showDeleted=true
  deletedAt?: string | null;
  deletedBy?: number | null;
  deletionReason?: string | null;
}

export interface MessageInput {
  descricao: string;
  dataInicio: string; // RFC3339
  dataFim: string;    // RFC3339
  estado: MessageEstado;
}

export interface ListMessagesResponse {
  messages: Message[];
  total: number;
  limit: number;
  offset: number;
}

// =============================================================================
// Centro Visit Types
// =============================================================================

export interface CentroVisit {
  id: number;
  participantId: number;
  participantName: string;
  digitalCenterId: number;
  digitalCenterName: string;
  checkedInAt: string;       // RFC3339
  checkedOutAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CheckInInput {
  qrCode?: string;
  digitalCenterId?: number;
}

export interface ListRecentCentroVisitsResponse {
  visits: CentroVisit[];
  limit: number;
}

export interface AlreadyCheckedInError {
  error: 'AlreadyCheckedIn';
  message: string;
  activeVisit: CentroVisit;
}

/** 422 body from POST /api/centros/check-in when outside the center's operating hours. */
export interface CentroClosedError {
  error: 'CentroClosed';
  message: string;
  /** Daily opening time in HH:MM (24h). */
  openingTime: string;
  /** Daily closing time in HH:MM (24h). */
  closingTime: string;
}

// =============================================================================
// Participant Equipment Assignment (Reservas) Types
// =============================================================================

export type AssignmentStatus = 'confirmed' | 'cancelled' | 'deleted';

export interface ParticipantEquipmentAssignment {
  id: number;
  participantId: number;
  participantName: string;
  equipmentId: number;
  equipmentName: string;
  startedAt: string;            // RFC3339
  endedAt?: string | null;
  /**
   * RFC3339 instant at which the API ends the session automatically
   * (startedAt + the equipment's slotUtilizacao). Absent when the equipment
   * has no time limit.
   */
  expiresAt?: string | null;
  status: AssignmentStatus;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string | null;
}

export interface AssignmentInput {
  participantId: number;
  equipmentId: number;
  startedAt: string;            // RFC3339
  endedAt?: string | null;
  status?: AssignmentStatus;
}

export interface ListAssignmentsResponse {
  assignments: ParticipantEquipmentAssignment[];
  total: number;
  limit: number;
  offset: number;
}

/** QR payload prefix encoded into equipment QR codes (see backend repository). */
export const EQUIPMENT_QR_PREFIX = 'AICD:equipment:';

/**
 * Parse an equipment QR payload (e.g. "AICD:equipment:42") into its numeric ID.
 * Returns null when the payload does not match the expected prefix or the tail
 * is not a positive integer.
 */
export function parseEquipmentQR(qr: string): number | null {
  if (typeof qr !== 'string' || !qr.startsWith(EQUIPMENT_QR_PREFIX)) {
    return null;
  }
  const tail = qr.slice(EQUIPMENT_QR_PREFIX.length);
  if (!/^\d+$/.test(tail)) {
    return null;
  }
  const id = Number.parseInt(tail, 10);
  return Number.isFinite(id) && id > 0 ? id : null;
}

// =============================================================================
// Activity Types (clubs, Circuitos, open days)
// =============================================================================

/**
 * The kinds of activity a centre runs. Mirrors models.ValidActivityTypes in
 * the Go API and the activity_type CHECK in migration 027.
 */
export type ActivityType = 'club' | 'circuit' | 'open_day';

export interface Activity {
  id: number;
  name: string;
  type: ActivityType;
  description?: string | null;
  universo?: BadgeUniverso | null;
  digitalCenterId: number;
  digitalCenterName: string;
  ownerUserId: number;
  ownerName: string;
  /** Badge awarded to the participants present; null when there is none. */
  badgeId?: string | null;
  badgeName?: string | null;
  syllabus?: string | null;
  tools?: string | null;
  /** Sessions not cancelled. */
  sessionCount: number;
  /** Start of the next session not yet over; null when none is planned. */
  nextSessionAt?: string | null;
  /** Start of the latest session, past or to come. For an open day, its date. */
  lastSessionAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ActivityInput {
  name: string;
  type: ActivityType;
  description?: string | null;
  universo?: BadgeUniverso | null;
  digitalCenterId: number;
  /** Defaults to the staff user making the request when omitted. */
  ownerUserId?: number;
  badgeId?: string | null;
  syllabus?: string | null;
  tools?: string | null;
  /**
   * On create only: when the activity happens. An open day is one event and
   * must say when it is; a Circuito may give its first visit, and a club its
   * first meeting and how it repeats.
   */
  session?: SessionCreateInput;
}

/** What creating an activity answers with: it, and the sessions created with it. */
export interface ActivityCreated extends Activity {
  sessions: ActivitySession[];
}

export interface ListActivitiesResponse {
  activities: Activity[];
  total: number;
  limit: number;
  offset: number;
}

export type ActivityValidationErrors = Partial<Record<keyof ActivityInput, string>>;

/** One dated occurrence of an activity. */
export interface ActivitySession {
  id: number;
  activityId: number;
  activityName: string;
  activityType: ActivityType;
  universo?: BadgeUniverso | null;
  digitalCenterId: number;
  digitalCenterName: string;
  badgeId?: string | null;
  badgeName?: string | null;
  leaderUserId: number;
  leaderName: string;
  startsAt: string; // RFC3339
  endsAt: string; // RFC3339
  /** Visiting group, counted rather than named. */
  visitorOrganization?: string | null;
  visitorContact?: string | null;
  visitorHeadcount?: number | null;
  notes?: string | null;
  /** Registered participants present. */
  attendanceCount: number;
  createdAt: string;
  updatedAt: string;
}

export type SessionRepeat = 'none' | 'weekly' | 'biweekly';

export interface SessionCreateInput {
  startsAt: string; // RFC3339
  endsAt: string; // RFC3339
  /** Defaults to the activity's owner when omitted. */
  leaderUserId?: number;
  repeat?: SessionRepeat;
  /** "YYYY-MM-DD", inclusive. Required when `repeat` is not 'none'. */
  repeatUntil?: string;
}

export interface SessionUpdateInput {
  startsAt: string;
  endsAt: string;
  leaderUserId: number;
  visitorOrganization?: string | null;
  visitorContact?: string | null;
  visitorHeadcount?: number | null;
  notes?: string | null;
}

export type SessionValidationErrors = Partial<
  Record<keyof SessionCreateInput | keyof SessionUpdateInput, string>
>;

export interface ListActivitySessionsResponse {
  sessions: ActivitySession[];
  total: number;
}

export interface ActivityAttendance {
  id: number;
  sessionId: number;
  participantId: number;
  participantName: string;
  joinedAt: string;
  /** Staff user who added the participant by hand; null for their own scan. */
  addedBy?: number | null;
}

/** What staff see when they open a session: the session, its QR and roster. */
export interface ActivitySessionDetail extends ActivitySession {
  joinUrl: string;
  /** Base64 data URL (data:image/png;base64,...). */
  qrCode: string;
  attendances: ActivityAttendance[];
}

export interface AwardBadgeResult {
  awarded: number;
  /** Participants present who already held the badge. */
  alreadyHeld: number;
}

/** What a participant sees after scanning a session's QR. */
export interface JoinedSession {
  sessionId: number;
  activityId: number;
  activityName: string;
  activityType: ActivityType;
  universo?: BadgeUniverso | null;
  description?: string | null;
  syllabus?: string | null;
  tools?: string | null;
  digitalCenterId: number;
  digitalCenterName: string;
  leaderName: string;
  startsAt: string;
  endsAt: string;
  joinedAt: string;
}

/** Stable `error` codes on a failed POST /api/activity-sessions/join. */
export type JoinSessionErrorCode =
  | 'SessionNotFound'
  | 'SessionNotStarted'
  | 'SessionEnded'
  | 'RemovedFromSession';

/** PWA route a session QR deep-links to (see ActivitySessionQRContent in the API). */
export const ACTIVITY_SESSION_JOIN_PATH = '/join/';

const JOIN_TOKEN_REGEX = /^[0-9a-f]{32}$/;

/**
 * Parse an activity session QR payload (e.g. "https://pwa.example.org/join/<token>")
 * into its join token. Returns null when the payload is not a session link.
 *
 * Matches on the path rather than a fixed host, so codes keep working when the
 * PWA moves domains. A trailing slash, query string or fragment is tolerated.
 */
export function parseActivitySessionQR(qr: string): string | null {
  if (typeof qr !== 'string') {
    return null;
  }
  const trimmed = qr.trim();
  const idx = trimmed.lastIndexOf(ACTIVITY_SESSION_JOIN_PATH);
  if (idx < 0) {
    return null;
  }
  const rest = trimmed.slice(idx + ACTIVITY_SESSION_JOIN_PATH.length);
  const token = rest.split('?')[0].split('#')[0].replace(/\/$/, '');
  return JOIN_TOKEN_REGEX.test(token) ? token : null;
}

// =============================================================================
// Presence Types (the front desk)
// =============================================================================

/** A piece of equipment a participant in the centre holds right now. */
export interface PresenceEquipment {
  assignmentId: number;
  equipmentId: number;
  equipmentName: string;
  startedAt: string;
  /** When the session ends on its own; null when there is no time limit. */
  expiresAt?: string | null;
}

/** A participant who is in a centre right now. */
export interface Presence {
  visitId: number;
  participantId: number;
  participantName: string;
  digitalCenterId: number;
  digitalCenterName: string;
  checkedInAt: string;
  /** Staff user who checked them in; null when they did it themselves. */
  checkedInBy?: number | null;
  checkedInByName?: string | null;
  equipment: PresenceEquipment[];
}

export interface ListPresencesResponse {
  presences: Presence[];
  total: number;
}

export interface PresenceCheckInInput {
  participantId: number;
  digitalCenterId: number;
}

export type PresenceEquipmentAction = 'start' | 'end';

export interface PresenceEquipmentInput {
  equipmentId: number;
  action: PresenceEquipmentAction;
}

/** Stable `error` codes the front desk endpoints answer with. */
export type PresenceErrorCode =
  | 'AlreadyCheckedIn'
  | 'ParticipantNotActive'
  | 'NoActiveVisit'
  | 'EquipmentInUse'
  | 'EquipmentNotFound'
  | 'EquipmentUnavailable'
  | 'NotOwner'
  | 'BadgeRequired'
  | 'NothingToDo'
  | 'NotInCentre';

// =============================================================================
// Health Check Types
// =============================================================================

export interface HealthResponse {
  status: 'healthy' | 'unhealthy';
  time: string;
}

export interface ReadyResponse {
  status: 'ready' | 'unhealthy';
  database: 'connected' | 'disconnected';
}

// =============================================================================
// Reports and Dashboard Types
// =============================================================================

/** The step a report's series advances by. */
export type ReportsBucket = 'day' | 'week' | 'month';

/** The headline figures of a period. */
export interface ReportsSummary {
  /** First and last day of the period, "YYYY-MM-DD", both included. */
  from: string;
  to: string;
  /** Check-ins at a centre. */
  visits: number;
  /** Those a member of staff made for a participant with no phone. */
  visitsByStaff: number;
  /** Different participants who checked in or were present at an activity. */
  uniqueParticipants: number;
  /** Activity sessions held: started, and not cancelled. */
  sessions: number;
  /** Participants present at those sessions. */
  attendance: number;
  /** Headcount of the visiting groups at those sessions. */
  visitors: number;
  equipmentHours: number;
  equipmentSessions: number;
  badgesAwarded: number;
  newParticipants: number;
}

/** One step of a report's series. */
export interface ReportsSeriesPoint {
  /** First day of the step, "YYYY-MM-DD". */
  start: string;
  visits: number;
  visitors: number;
  attendance: number;
  equipmentHours: number;
}

export interface CenterReportRow {
  id: number;
  name: string;
  visits: number;
  attendance: number;
  visitors: number;
  equipmentHours: number;
}

/** The activity figures of one type or one universo. */
export interface ActivityBreakdown {
  /** The activity type, or the universo; empty for "no universo". */
  key: string;
  activities: number;
  sessions: number;
  attendance: number;
  visitors: number;
}

export interface ActivityReportRow {
  id: number;
  name: string;
  type: ActivityType;
  universo: BadgeUniverso | null;
  digitalCenterName: string;
  sessions: number;
  attendance: number;
  visitors: number;
}

export interface EquipmentReportRow {
  id: number;
  name: string;
  category: string;
  digitalCenterName: string;
  sessions: number;
  participants: number;
  hours: number;
}

export interface BadgeReportRow {
  id: string;
  name: string;
  universo: BadgeUniverso | null;
  awarded: number;
}

export interface AgeGroupStats {
  /** 0 to 14. */
  criancas: number;
  /** 15 to 24. */
  jovens: number;
  /** 25 to 64. */
  adultos: number;
  /** 65 and over. */
  seniores: number;
  total: number;
}

export interface GenderStats {
  masculino: number;
  feminino: number;
  indefinido: number;
  total: number;
}

/** Response body of GET /api/reports/overview. */
export interface ReportsOverview {
  bucket: ReportsBucket;
  summary: ReportsSummary;
  /** The period of the same length that came just before. */
  previous: ReportsSummary;
  series: ReportsSeriesPoint[];
  byCenter: CenterReportRow[];
  byType: ActivityBreakdown[];
  byUniverso: ActivityBreakdown[];
  activities: ActivityReportRow[];
  equipment: EquipmentReportRow[];
  badges: BadgeReportRow[];
  /** Of the participants who came in the period, not of everyone registered. */
  ageGroups: AgeGroupStats;
  gender: GenderStats;
}

export interface DashboardSession {
  id: number;
  activityId: number;
  activityName: string;
  activityType: ActivityType;
  universo: BadgeUniverso | null;
  leaderName: string;
  startsAt: string;
  endsAt: string;
  attendanceCount: number;
  visitors: number;
}

export interface DashboardEquipmentSession {
  id: number;
  participantName: string;
  equipmentName: string;
  startedAt: string;
  /** When the session ends on its own; null when there is no limit. */
  expiresAt: string | null;
}

export interface DashboardEquipmentUse {
  name: string;
  hours: number;
}

/** Response body of GET /api/reports/dashboard: what is happening today. */
export interface Dashboard {
  presentNow: number;
  visitsToday: number;
  visitsYesterday: number;
  /** Counted in units: a box of eighteen boards is eighteen. */
  equipmentUnits: number;
  equipmentUnitsInUse: number;
  equipmentHoursToday: number;
  equipmentHoursWeek: number;
  sessionsToday: DashboardSession[];
  equipmentSessions: DashboardEquipmentSession[];
  /** Over the last 30 days. */
  topEquipment: DashboardEquipmentUse[];
  /** What waits for someone to act, in every centre. */
  pending: {
    consents: number;
    staffAccounts: number;
  };
}

// =============================================================================
// Percurso Types
// =============================================================================

/** What happened at one step of a participant's percurso. */
export type PercursoStepKind = 'visit' | 'session' | 'badge' | 'equipment';

export interface PercursoStep {
  kind: PercursoStepKind;
  at: string;
  /** Null for a badge, and for a visit or equipment session still open. */
  endedAt: string | null;
  /** The activity, badge or equipment name; for a visit, the centre. */
  title: string;
  /** The centre it happened in; empty for a badge. */
  place: string;
  universo: BadgeUniverso | null;
  activityType: ActivityType | null;
}

/** One day on the percurso, its steps in the order they happened. */
export interface PercursoStop {
  /** "YYYY-MM-DD", in Europe/Lisbon. */
  day: string;
  steps: PercursoStep[];
}

export interface PercursoUniverso {
  universo: BadgeUniverso;
  sessions: number;
  badges: number;
}

export interface PercursoTotals {
  days: number;
  visits: number;
  sessions: number;
  badges: number;
  equipmentHours: number;
  /** The universos touched, in the brand's display order. */
  universos: PercursoUniverso[];
}

/** GET /api/participants/{id}/percurso. Stops run from the first day to the latest. */
export interface Percurso {
  stops: PercursoStop[];
  totals: PercursoTotals;
}

// =============================================================================
// Guardian (Encarregado de Educação) Types
// =============================================================================

export type GuardianEstado = 'Pendente' | 'Ativo' | 'Inativo';

/** An Encarregado de Educação's account. */
export interface Guardian {
  id: number;
  nome: string;
  apelido: string;
  email: string;
  telemovel: string;
  estado: GuardianEstado;
  approvedAt: string | null;
  approvedByName: string | null;
  createdAt: string;
  updatedAt: string;
  /** Participants linked now. Filled in staff lists only. */
  educandos: number;
}

/** POST /api/auth/guardian/register. Answers 201 { status: 'pending', guardianId }. */
export interface GuardianRegisterInput {
  nome: string;
  apelido: string;
  email: string;
  telemovel: string;
  password: string;
  captchaToken?: string;
}

/** POST /api/auth/guardian/login. A pending account answers 403 code PENDING_APPROVAL. */
export interface GuardianLoginInput {
  email: string;
  password: string;
}

export interface GuardianAuthResponse {
  user: Guardian;
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

/** Where a participant is checked in right now. */
export interface GuardianPresence {
  digitalCenterName: string;
  since: string;
}

/** A participant as their guardian sees them. */
export interface Educando {
  id: number;
  nome: string;
  apelido: string;
  dataNascimento: string;
  digitalCenterId: number | null;
  digitalCenterName: string | null;
  /** The home centre's certificate artwork key, for the child's certificates. */
  certificateTemplate: string | null;
  /** Null when not at a centre now. */
  presence: GuardianPresence | null;
  linkedAt: string;
}

/** GET /api/guardian/educandos */
export interface ListEducandosResponse {
  educandos: Educando[];
}

/** A participant linked to a guardian, as staff see it. */
export interface GuardianLink {
  participantId: number;
  participantName: string;
  dataNascimento: string;
  digitalCenterName: string | null;
  linkedAt: string;
  linkedByName: string;
}

/** A participant whose recorded encarregado's email or phone is the guardian's. */
export interface GuardianSuggestion {
  participantId: number;
  participantName: string;
  dataNascimento: string;
  digitalCenterName: string | null;
  /** 'email', 'telemovel' or 'email,telemovel'. */
  matchedOn: string;
}

/** GET /api/guardians/{id}; also the answer to activate, deactivate, link and unlink. */
export interface GuardianDetail {
  guardian: Guardian;
  educandos: GuardianLink[];
  suggestions: GuardianSuggestion[];
}

/** GET /api/guardians?estado= */
export interface ListGuardiansResponse {
  guardians: Guardian[];
  total: number;
}

// =============================================================================
// Recommendation Types
// =============================================================================

/** peers: Diggers with a similar percurso went to it; popular: among the most attended. */
export type RecommendationReason = 'peers' | 'popular';

/** One suggested activity. */
export interface Recommendation {
  activityId: number;
  name: string;
  type: ActivityType;
  universo: BadgeUniverso | null;
  digitalCenterName: string;
  nextSessionAt: string;
  reason: RecommendationReason;
  /** Diggers like this one who went (peers), or everyone who did lately (popular). */
  peers: number;
  /** The participant's own activity those peers share; null when popular. */
  becauseActivity: string | null;
}

/** GET /api/participants/{id}/recommendations */
export interface Recommendations {
  /** False while suggestions are switched off on the server. */
  enabled: boolean;
  /** False while there is too little history to suggest from. */
  ready: boolean;
  suggestions: Recommendation[];
}

// =============================================================================
// Privacy request Types
// =============================================================================

/** What a Digger or an Encarregado asks about their own data (RGPD). */
export type PrivacyRequestKind = 'copy' | 'erasure';

/** A request made from the PWA; staff answer it within a month. */
export interface PrivacyRequest {
  id: number;
  kind: PrivacyRequestKind;
  message: string | null;
  participantId: number | null;
  guardianId: number | null;
  /** The person's name now; "Participante anonimizado" once erased. */
  subjectName: string;
  createdAt: string;
  /** createdAt plus one month: when the answer is due. */
  dueAt: string;
  handledAt: string | null;
  handledByName: string | null;
}

export interface PrivacyRequestInput {
  kind: PrivacyRequestKind;
  message?: string | null;
}

export interface ListPrivacyRequestsResponse {
  requests: PrivacyRequest[];
}
